import {Injectable} from "@angular/core";
import {ActivatedRoute, RouterState} from "@angular/router";
import {DxLogger} from "../../dx-core/services/dx-logger.service";
import {DxNavigatorOptions} from "../domain/ui-options/dx-navigator/dx-navigator-options";
import {DxNavigationTreeNode} from "../domain/navigator/dx-navigation-tree-node";
import {DxNavigatorModeType} from "../domain/navigator/dx-navigator-mode-type";
import {DxNavigationTreeNodeDataProvider} from "../domain/navigator/dx-navigation-tree-node-data-provider";


/**
 * Сервис-модель для навигаторв.
 * Провайдится на уровне навигатора. Не на уровне модуля!
 * Зачем:
 * 1. По мере условжнения логики работы компонента происходит захламление контроллеров утильными алгоримами.
 *
 * 2. Усложнение логики работы\использования навигатора может потребовать сервисной интеграции между частями.
 *    В этом случае если вся логика будет раскидана по компонентам, то с этим могут быть проблемы.
 *
 * 3. Переиспользование алгоримов в разных составных частях навигатора во избежание дублирования кода.
 */
@Injectable()
export class DxNavigatorService {
  /** Опции навигатора. Хранение опций в сервисе делает их доступными для всех комопонентов навигатора. */
  private _options: DxNavigatorOptions;

  /**
   * Фиктивный, никчему не привязанный узел для домашнего раздела.
   * Используется для унификации вызовов методов.
   */
  private _homeNode: DxNavigationTreeNode;

  /**
   * Мапа, устанавливающшая соотвествие между элементом верхнего навигационного меню и его
   * "Деревом подразделов". Используется только в режиме работы "Шапка всему голова"
   */
  private _subMenusMap: Map<number, DxNavigationTreeNodeDataProvider[]>;

  /**
   * Конфиг роутинга для не-lazy модулей.
   * Это конфиг роутера определенный в не-лэйзи загружаемых модулях: app-routing, другие не-лайзи роутинг модулю.
   */
  private _eagerRouterConfig: any;

  /**
   * Роты, которые должны открываться через навигатор.
   * Эти роуты должны быть определены как дочерние роуты рота, на котором "висит" сам навигатор или его обертка.
   */
  private _configuredRoutes: Map<string, any> = new Map();

  /**
   * Активированные роуты. Пополняется по мере осуществления навигации.
   * Сделана для вычисления роута, относительно которого нужно осуществить следующий навигационный переход.
   */
  private _activatedRoutes: Map<string, ActivatedRoute> = new Map();

  /** Активированный роут для навигатора. */
  private _rootActivatedRoute: ActivatedRoute;

  /** Сегменты url для восстановления состояния */
  private _segments: string[];


  constructor(private _logger: DxLogger) {}


  get options(): DxNavigatorOptions {
    return this._options;
  }

  set options(value: DxNavigatorOptions) {
    this._options = value;
  }

  get eagerRouterConfig(): any {
    return this._eagerRouterConfig;
  }

  set eagerRouterConfig(value: any) {
    this._eagerRouterConfig = value;
  }

  get rootActivatedRoute(): ActivatedRoute {
    return this._rootActivatedRoute;
  }

  set rootActivatedRoute(value: ActivatedRoute) {
    this._rootActivatedRoute = value;
  }

  get configuredRoutes(): Map<string, any> {
    return this._configuredRoutes;
  }

  get homeNode(): DxNavigationTreeNode {
    return this._homeNode;
  }

  get segments(): string[] {
    return this._segments;
  }

  set segments(value: string[]) {
    this._segments = value;
  }

  set subMenusMap(value: Map<number, DxNavigationTreeNodeDataProvider[]>) {
    this._subMenusMap = value;
  }

  public init(): void {
    if(this._options.homeRoute) {
      this._homeNode = new DxNavigationTreeNode(null);
      this._homeNode.level = 1;
      this._homeNode.route = this._options.homeRoute;
    }
  }


  public get navigatorRoute(): any {
    return this._eagerRouterConfig.find(route => route.path == this._options.navigatorRoute);
  }


  public clearData(): void {
    this._options = null;
    this._eagerRouterConfig = null;
    this._rootActivatedRoute = null;
    this._configuredRoutes.clear();
    this._activatedRoutes.clear();
    this._subMenusMap.clear();
    this._segments = null;
  }


  public clearSegments(): void {
    this._segments = null;
  }


  /**
   * Рекурсивно траверсирунет конфигурацию роута, пополняя мапу сконфигурированных в приложении роутов.
   * По сути, таким образом происходит вормирование глобального множества ротов для всех lazy- и не-lazy модулей.
   */
  public collectRoutes(configuredRoutes: Map<string, any>, routeConfig): void {
    if(!this._configuredRoutes.has(routeConfig.path)) this._configuredRoutes.set(routeConfig.path, routeConfig);
    if(routeConfig.children && routeConfig.children.length > 0) {
      let child: any;
      for(let i = 0; i < routeConfig.children.length; i++) {
        child = routeConfig.children[i];
        if(!configuredRoutes.has(child.path)) configuredRoutes.set(child.path, child);
        if(child.children && child.children.length > 0) {
          this.collectRoutes(this._configuredRoutes, child.children);
        }
      }
    }
  }


  /**
   * Рекурсивно траверсирует дерево активированных роутов, обновляя мапу активированных роутов.
   */
  private _collectActivatedRoutes(activatedRoutes: Map<string, ActivatedRoute>, children: ActivatedRoute[]): void {
    let child: any;
    for(let i = 0; i < children.length; i++) {
      child = children[i];
      this.collectRoutes(this._configuredRoutes, child.routeConfig);

      const path = child.routeConfig.path;
      if(!this._activatedRoutes.has(path)) {
        this._activatedRoutes.set(path, child);
      }
      // Почему нельзя перенести запуск рекурсии в блок if выше дабы ограничить количество вызовов.
      // Роут может содеражать lazy-модули в качестве детей, поэтому от такта к такту навигации состояние
      // дерева может менятся и дети динамически загружаться.
      if(child.children && child.children.length > 0) {
        this._collectActivatedRoutes(activatedRoutes, child.children);
      }
    }
  }


  /**
   * Через рекурсию обновляет мапу дочерних дл навигатора активированных роутов.
   * Выполнять это обновление
   */
  public updateActivatedRoutes(routerState: RouterState): void {
    // Сам рут не нужно анализировать! У него routerConfig нул.
    if(routerState.root.children && routerState.root.children.length) {
      this._collectActivatedRoutes(this._activatedRoutes, routerState.root.children);
    }
  }


  /**
   * Метод определяет, приведет ли открытие роута переданной ноды к загрузке нового модуля.
   */
  public isLazyModuleLoaded(route: string): boolean {
    // Находим роут навигатора в не-лэйзи конфиге роутера
    const navigatorRoute = this._eagerRouterConfig.find(route => route.path == this._options.navigatorRoute);

    // Находим среди дочерних роутов навигатора лэйзи-модули
    const lazyModules = navigatorRoute.children.filter(child => child.loadChildren !== null);
    // Проверяем налич
    if(lazyModules && lazyModules.length > 0) {
      return !!lazyModules.find(item => item.path === route);
    }
    return false;
  }


  /**
   * Билдит абсолютную url для навигационного перехода на основе
   * 1. Режима работы навигатора
   * 2. Собственно нода
   * 3. Источника события навигации: шапка \ боковая панель
   *
   * Почему для навигации использован метод `navigateByUrl` а не `navigateByUrl`?
   * Ответ:
   * Как показала правктика метод `navigateByUrl` по неизвестной причине перестает навигировать по дочерним роутам
   * lazy-модуля если этому предшествовало переключение между разделами, представляющими собой lazy-модулями.
   * После половины дня в гугле я так и не шаел возможной причины этого поведения.
   */
  public buildNavigationUrl(node: DxNavigationTreeNode, isHeadNavigation: boolean, sidePanelOwner: DxNavigationTreeNode): string {
    let url: string = '/' + this._options.navigatorRoute;
    switch (this._options.mode) {
      case DxNavigatorModeType.TOP: {
        url += '/' + node.nodeUrl;
        break;
      }
      case DxNavigatorModeType.SIDE: {
        url += '/' + node.nodeUrl;
        break;
      }
      case DxNavigatorModeType.TOP_SIDE: {
        if(isHeadNavigation) {
          url += '/' + node.nodeUrl;
        } else {
          if(sidePanelOwner && sidePanelOwner.id !== node.id) url += '/' + sidePanelOwner.route;
          url += '/' + node.nodeUrl;
        }
        break;
      }
    }
    return url;
  }


  /**
   * Находит родителький роут, относительно которого должен быть открыт дочерний роут.
   * Скрижали навигации:
   * 1. Элементы верхнего меню с ровнем 1 должны резовится относительно роута `navigator`
   * 2. Элементы бокового меню при наличии верхнего меню являются зависимымми и должны
   *    резолвится относительно своего родителя:
   *    2.1. Для нодов первого уровня это элемент меню верхнего уровня
   *    2.2. Для нодов уровня больше одного это родитель из бокового меню
   *
   * 3. Элементы бокового меню при отсутствии верхнего меню должны резовится относительно роута `navigator`
   */
  public findRelativeRoute(node: DxNavigationTreeNode, isHeadNavigation: boolean, sidePanelOwner: DxNavigationTreeNode): ActivatedRoute {
    let relativeToRoute: ActivatedRoute;
    switch (this._options.mode) {
      case DxNavigatorModeType.TOP: {
        relativeToRoute = this._findRelativeRouteForSingleDimCase(node);
        break;
      }
      case DxNavigatorModeType.SIDE: {
        relativeToRoute = this._findRelativeRouteForSingleDimCase(node);
        break;
      }
      case DxNavigatorModeType.TOP_SIDE: {
        relativeToRoute = this._findRelativeRouteForDoubleDimsCase(node, isHeadNavigation, sidePanelOwner);
        break;
      }
      default:
        relativeToRoute = this._rootActivatedRoute;
    }
    return relativeToRoute;
  }


  private _findRelativeRouteForSingleDimCase(node: DxNavigationTreeNode): ActivatedRoute {
    if(node.level === 1) {
      return this._rootActivatedRoute;
    } else if(node.level > 1) {
      return this._activatedRoutes.get(node.parent.route);
    }
  }


  private _findRelativeRouteForDoubleDimsCase(node: DxNavigationTreeNode, isHeadNavigation: boolean, sidePanelOwner: DxNavigationTreeNode): ActivatedRoute {
    let relativeToRoute: ActivatedRoute;
    if(isHeadNavigation) {
      if(node.level === 1) {
        relativeToRoute = this._rootActivatedRoute;
      } else if(node.level > 1) {
        relativeToRoute = this._activatedRoutes.get(node.parent.route);
      }
    } else {
      if(node.level === 1) {
        // А вот для первого ровня все интересно, так как деревья в шапке и подразделах это разные деревья.
        // Для ноды первого уровня в этом случае нужно найти объект `ActivatedRoute`, соотвествующий
        // ноде верхнего меню для которой был открыт подраздел.
        relativeToRoute = this._activatedRoutes.get(sidePanelOwner.route);
      } else if(node.level > 1) {
        // С нодами второго уровня возникает следующий нюанс.
        // Элемент первого уровня может представлять собой просто группирующий нод (считай папка).
        // То есть для такой ноды не надо делать компонент и открывать его в контенте.
        // В контенте в этом случае нужно открывать компоненты для нод второго и более уровня.
        // А открываться нода ВТОРОГО УРОВНЯ должна относительно нода-владельца панели.
        // Другими словами, если родитель ноды второго уровня не участвуюет в роутинге, то
        // ноду второго уровня резолвим относительно ноды-овнера.
        if(node.level === 2 && !node.parent.involvedInRouting) {
          relativeToRoute = this._activatedRoutes.get(sidePanelOwner.route);
        }
        // Иначе резовим относительно ActivatedRoute родителя. Он должен быть, так как роут родителя
        // был активирован на предыдущем такте навигации.
        else {
          relativeToRoute = this._activatedRoutes.get(node.parent.route);
        }
      }
    }
    return relativeToRoute;
  }


  public needRestoreState(segments: string[]): boolean {
    const lastSegment = segments[segments.length - 1];
    return lastSegment !== this._options.homeRoute && lastSegment !== this._options.navigatorRoute;
  }


  public needRestoreSideMenuState(headMenuNode: DxNavigationTreeNode, segments: string[]): boolean {
    if(this._subMenusMap.has(headMenuNode.id)) {
      let items = this._subMenusMap.get(headMenuNode.id);
      items = items.filter(item => segments.indexOf(item.getNodeRoute()) !== -1);
      return items.length > 0;
    }
    return false;
  }


  private _traceCollectedRoutes(): void {
    this._logger.debug('==============================================================================');
    this._logger.debug('Collected activated routes: ');
    let routes: string[] = [];
    this._activatedRoutes.forEach((value, key) => {
      routes.push(key);
    });
    this._logger.debug(routes.join(" || "));

    this._logger.debug('');
    this._logger.debug('Collected routes: ');
    routes = [];
    this._configuredRoutes.forEach((value, key) => {
      routes.push(key);
    });
    this._logger.debug(routes.join(" || "));
  }
}
